import { fetchProfiles } from '../lib/profileCache'
import { dayKey, todayKey, nextDay } from '../lib/datetime';
import { clickable } from '../lib/a11y'
import './Skeleton.css'
// ── Tonight Mode ──────────────────────────────────────────────────────────────
// "Plan to Watch" social commitment system.
//
// useWatchPlans(user)      — hook for managing the current user's plans
// WatchPlanButton          — toggleable 🌙 button on episode rows
// TonightWidget            — home screen glowing card showing tonight's lineup
// PlansCheckIn             — batched "did you watch it?" check-in sheet
// FriendPlansWidget        — what your friends are watching tonight

import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from 'react'
import { createPortal } from 'react-dom'
import { MoonStar } from 'lucide-react'
import { MoonIcon } from './MoonIcon'
import { PlansCheckIn } from './PlansCheckIn'
import { Avatar } from './Avatar'
import { usePromptSlot } from '../hooks/usePromptSlot'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { postFn } from '../lib/postFn'
import { nextProgressAfter } from '../lib/episode'
import { ChevronDown, Check } from 'lucide-react'
import { useUiPrefs } from '../hooks/useUiPrefs'
import { Collapse } from './UI'
import { StreamingLaunchButton } from './StreamingLaunchButton'
import { Clapboard } from './ClapboardIcon'
import { SwipeRow } from './SwipeRow'
import { isSnoozedToday, snoozeToday } from '../lib/checkinSnooze'

// ── Context ────────────────────────────────────────────────────────────────────
const PlansCtx = createContext(null)
export const usePlans = () => useContext(PlansCtx)

// Plans render in AIRING order — grouped by show, then season, then episode — so a
// Run slice like S2·E5–E7 reads E5, E6, E7 instead of whatever order the rows were
// inserted in (Jason 2026-07-03: Tonight was showing add-order, not airing order).
const byAiring = (a, b) =>
  (a.show_id - b.show_id) || (a.season - b.season) || (a.episode - b.episode)
// --- Check-in timing ---------------------------------------------------------
// A tonight plan is for the evening, so the check-in prompt must not fire early.
// We hold it until the local clock reaches CHECKIN_HOUR. Save it for later mutes
// a plan for the rest of the local day via a tiny localStorage snooze; the plan
// itself is never touched, so it stays in your lineup and stays visible to friends.
const CHECKIN_HOUR = 20
// isSnoozedToday / snoozeToday live in lib/checkinSnooze.js — not here — so
// useAuth.jsx (which syncs today's snoozes from the account on every profile
// load) can share the same read/write functions instead of a device-local copy.

// ── Hook: useWatchPlans ────────────────────────────────────────────────────────
export function useWatchPlans(user, promoteFromPlan) {
  const [plans,   setPlans]   = useState([])  // today's pending plans
  const [futurePlans, setFuturePlans] = useState([]) // pending plans dated AHEAD of today
  const [loaded,  setLoaded]  = useState(false)
  const [today, setToday] = useState(todayKey) // YYYY-MM-DD (user local day)
  useEffect(() => {
    const tick = () => setToday(todayKey())
    document.addEventListener('visibilitychange', tick)
    window.addEventListener('focus', tick)
    return () => { document.removeEventListener('visibilitychange', tick); window.removeEventListener('focus', tick) }
  }, [])

  const load = useCallback(async () => {
    if (!user?.id) return
    // Roll forward FIRST: an unwatched pending plan from a past day is NOT dead —
    // carry it to today so "My Plans" is a persistent backlog you manage, never a
    // list that silently deletes itself overnight (Jason 2026-06-21). Only pending
    // rows move; confirmed/skipped stay put as history. Done before the select so
    // the carried-over plans appear in today's set immediately.
    // Remember which rows are being rolled: only THOSE get deduped per show below. A
    // run's slice legitimately puts several episodes of one show on tonight.
    const { data: rolling } = await supabase.from('watch_plans').select('id').eq('user_id', user.id).eq('status', 'pending').lt('planned_date', today)
    const rolledIds = new Set((rolling || []).map(r => r.id))
    if (rolledIds.size) {
      await supabase.from('watch_plans').update({ planned_date: today })
        .in('id', [...rolledIds])
        .then(({ error: e }) => { if (e) console.warn('plan roll-forward:', e.message) })
    }
    // Today + future-dated selects are independent: issue both at once (boot cost).
    const [{ data }, { data: fut }] = await Promise.all([
      supabase.from('watch_plans').select('*').eq('user_id', user.id).eq('planned_date', today),
      supabase.from('watch_plans').select('*').eq('user_id', user.id).eq('status', 'pending').gt('planned_date', today),
    ])
    // One pending plan per show. Roll-forward is "never lose a plan", but an old flow
    // left one row per EPISODE (Jason's Tonight showed 46 South Park rows, 2026-09-16),
    // and each visit carried all of them to today again. Keep the earliest episode's
    // plan, mark the rest skipped, and never render the duplicates.
    const rows = (data || []).slice()
    const dupes = []
    const seen = new Map()
    for (const p of rows.filter(r => r.status === 'pending' && rolledIds.has(r.id)).sort((x, y) => (x.season - y.season) || (x.episode - y.episode))) {
      if (seen.has(p.show_id)) dupes.push(p.id); else seen.set(p.show_id, p.id)
    }
    if (dupes.length) {
      supabase.from('watch_plans').update({ status: 'skipped' }).in('id', dupes).then(() => {}).catch(() => {})
    }
    setPlans(rows.filter(r => !dupes.includes(r.id)).sort(byAiring))
    // Future-dated plans (queued ahead from the calendar for an episode that hasn't
    // aired yet). Kept OUT of today's set so the evening check-in never nags about an
    // episode that isn't out — but tracked so the plan buttons reflect their state and
    // a second tap un-plans instead of duplicating. They roll into `plans` naturally on
    // their air day via the roll-forward above (Jason 2026-07-10: plan upcoming eps).
    setFuturePlans(fut || [])
    setLoaded(true)
  }, [user?.id, today])

  useEffect(() => { load() }, [load])

  // Auto-refresh when an episode is watched that matches a pending plan
  useEffect(() => {
    const onConfirmed = (e) => {
      const { showId, season, episode } = e.detail || {}
      setPlans(prev => prev.map(p =>
        p.show_id === showId && p.status === 'pending' &&
        (p.season < season || (p.season === season && p.episode <= episode))
          ? { ...p, status: 'confirmed' }
          : p
      ))
    }
    window.addEventListener('nextep:plan_confirmed_via_episode', onConfirmed)
    // A reopened plan (un-mark) may have a past planned_date; load() rolls it
    // forward to today and refetches, so it returns to the lineup.
    const onReopened = () => load()
    window.addEventListener('nextep:plan_reopened', onReopened)
    return () => {
      window.removeEventListener('nextep:plan_confirmed_via_episode', onConfirmed)
      window.removeEventListener('nextep:plan_reopened', onReopened)
    }
  }, [load])

  // Toggle a plan for a specific episode. plannedDate defaults to today ("watch tonight");
  // pass a future YYYY-MM-DD (the episode's air date) to queue an upcoming episode ahead —
  // it lands in Tonight on that day instead of nagging tonight.
  const toggle = useCallback(async (show, season, episode, episodeName, plannedDate) => {
    if (!user?.id) return
    const when     = plannedDate || today
    const isFuture = when > today
    // A plan for this episode may live in either set — search both so a second tap
    // always un-plans rather than creating a duplicate.
    const matches = (p) => p.show_id === show.id && p.season === season && p.episode === episode
    const existing = [...plans, ...futurePlans].find(p => matches(p) && p.status === 'pending')
    if (existing) {
      // Remove plan — optimistic: drop it from state immediately so the moon
      // un-fills on tap, then reconcile with the DB (restore on failure).
      const dropIt = (arr) => arr.filter(p => p.id !== existing.id)
      setPlans(dropIt); setFuturePlans(dropIt)
      const { error } = await supabase.from('watch_plans').delete().eq('id', existing.id)
      if (error) {
        const restore = (arr) => [...arr, existing].sort(byAiring)
        if (existing.planned_date > today) setFuturePlans(restore); else setPlans(restore)
      }
      return false // plan removed
    }
    const ghost = plans.find(p => matches(p) && p.planned_date === when)
    if (ghost) {
      const revived = { ...ghost, status: 'pending', confirmed_at: null }
      setPlans(prev => prev.map(p => p.id === ghost.id ? revived : p))
      const { error } = await supabase.from('watch_plans').update({ status: 'pending', confirmed_at: null }).eq('id', ghost.id)
      if (error) { setPlans(prev => prev.map(p => p.id === ghost.id ? ghost : p)); return null }
      try { window.dispatchEvent(new CustomEvent('nextep:plan-added', { detail: { showId: show?.id, season, episode, plannedDate } })) } catch {}
      promoteFromPlan?.(show.id)
      return true
    } else {
      // Add plan — optimistic: insert a placeholder into state right away (moon
      // fills on tap), then swap in the real row, or drop the placeholder on error.
      const tmpId = `tmp-${show.id}-${season}-${episode}`
      const optimistic = {
        id: tmpId, user_id: user.id, show_id: show.id,
        show_data: { name: show.name, poster_path: show.poster_path, backdrop_path: show.backdrop_path },
        season, episode, episode_name: episodeName || null, planned_date: when, status: 'pending',
      }
      if (isFuture) setFuturePlans(prev => [...prev, optimistic])
      else          setPlans(prev => [...prev, optimistic].sort(byAiring))

      const { data } = await supabase.from('watch_plans').insert({
        user_id:      user.id,
        show_id:      show.id,
        show_data:    { name: show.name, poster_path: show.poster_path, backdrop_path: show.backdrop_path },
        season,
        episode,
        episode_name: episodeName || null,
        planned_date: when,
        status:       'pending',
      }).select().single()
      if (data) {
        // Replace the placeholder with the real row (keeps its real id for later ops).
        const swap = (arr) => arr.map(p => p.id === tmpId ? data : p).sort(byAiring)
        if (isFuture) setFuturePlans(swap); else setPlans(swap)
        try { window.dispatchEvent(new CustomEvent('nextep:plan-added', { detail: { showId: show?.id, season, episode, plannedDate } })) } catch {}
        // Planning an episode signals intent to watch → promote a plan_to_watch show to
        // 'watching' (no-op for any other status), so a show you've queued for Tonight
        // stops reading "Plan to Watch". Mirrors saveProgress's auto-upgrade on a mark.
        promoteFromPlan?.(show.id)
        // Log activity for social feed
        await supabase.from('activities').insert({
          user_id:    user.id,
          type:       'plan_created',
          show_id:    show.id,
          show_name:  show.name,
          show_poster: show.poster_path,
          data: { season, episode, episode_name: episodeName, planned_date: when },
        })
        // Push notification to followers/following
        // Fire-and-forget — don't block UI on notification delivery
        postFn('/.netlify/functions/notify-activity', {
          type: 'plan_created',
          show: { id: show.id, name: show.name, poster_path: show.poster_path },
          extra: {
            season, episode,
            episode_name: episodeName,
            message: isFuture
              ? `is planning to watch ${show.name} S${season}·E${episode} 📅`
              : `is planning to watch ${show.name} S${season}·E${episode} tonight 🌙`,
          },
        }).catch(() => {}) // silent fail in Vite dev
      } else {
        // Insert failed — drop the optimistic placeholder so the moon un-fills.
        const dropTmp = (arr) => arr.filter(p => p.id !== tmpId)
        if (isFuture) setFuturePlans(dropTmp); else setPlans(dropTmp)
        return null
      }
      return true // plan added
    }
  }, [user?.id, plans, futurePlans, today, promoteFromPlan])

  // Confirm watched — called from PlansCheckIn
  const confirm = useCallback(async (plan) => {
    const { error } = await supabase.from('watch_plans')
      .update({ status: 'confirmed', confirmed_at: new Date().toISOString() })
      .eq('id', plan.id)
    if (!error) {
      setPlans(prev => prev.map(p => p.id === plan.id ? { ...p, status: 'confirmed' } : p))
      // Log confirmation activity
      await supabase.from('activities').insert({
        user_id:    user.id,
        type:       'plan_confirmed',
        show_id:    plan.show_id,
        show_name:  plan.show_data?.name,
        show_poster: plan.show_data?.poster_path,
        data: { season: plan.season, episode: plan.episode, episode_name: plan.episode_name },
      })
      // Social notification — "Jason watched The Boys S4·E3!"
      postFn('/.netlify/functions/notify-activity', {
        type: 'plan_confirmed',
        show: { id: plan.show_id, name: plan.show_data?.name, poster_path: plan.show_data?.poster_path },
        extra: {
          season: plan.season,
          episode: plan.episode,
          episode_name: plan.episode_name,
          message: `just watched ${plan.show_data?.name} S${plan.season}·E${plan.episode} 🎬`,
        },
      }).catch(() => {}) // silent fail in Vite dev
    }
    return !error
  }, [user?.id])

  // Batch confirm (the consolidated check-in) — one query, one local update, one
  // summary notification instead of N. Progress advancement is handled by the
  // caller via saveProgress; this just settles plan status so nothing re-nags.
  const confirmMany = useCallback(async (planIds) => {
    if (!user?.id || !planIds?.length) return
    await supabase.from('watch_plans')
      .update({ status: 'confirmed', confirmed_at: new Date().toISOString() })
      .in('id', planIds)
    setPlans(prev => prev.map(p => planIds.includes(p.id) ? { ...p, status: 'confirmed' } : p))
  }, [user?.id])

  // Skip — no shame, no notification
  const skip = useCallback(async (planId) => {
    await supabase.from('watch_plans').update({ status: 'skipped' }).eq('id', planId)
    setPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'skipped' } : p))
  }, [])

  // Save it for later — reschedule to tomorrow so the check-in stops nagging today
  // (and survives a refresh; it resurfaces fresh tomorrow).
  const later = useCallback(async (planId) => {
    const tomorrow = nextDay(today)
    const { error } = await supabase.from('watch_plans').update({ planned_date: tomorrow }).eq('id', planId)
    if (error) return
    const moved = plans.find(p => p.id === planId)
    setPlans(prev => prev.filter(p => p.id !== planId))
    if (moved) setFuturePlans(prev => [...prev.filter(p => p.id !== planId), { ...moved, planned_date: tomorrow }])
  }, [today, plans])

  const isPlanned = useCallback((showId, season, episode) =>
    [...plans, ...futurePlans].some(p => p.show_id === showId && p.season === season && p.episode === episode && p.status === 'pending'),
    [plans, futurePlans])

  // Memoized so their identity is stable across renders (prevents effect churn).
  const todayPending = useMemo(() => plans.filter(p => p.status === 'pending'), [plans])
  const needsCheckIn = useMemo(() => {
    // Never nag early: a tonight plan is for the evening, so hold the prompt
    // until the local clock reaches CHECKIN_HOUR. Give a freshly added plan an
    // hour before asking, and skip anything snoozed for today (Save it for later).
    if (new Date().getHours() < CHECKIN_HOUR) return []
    const todayK = todayKey()
    return plans.filter(p =>
      p.status === 'pending' &&
      // Only TODAY's freshly-made plans trigger the evening check-in. A rolled-over
      // backlog plan keeps planned_date=today but its created_at is old, so it used
      // to pass the 1hr gate and re-nag EVERY evening forever (Jason 2026-06-28).
      // Backlog now lives quietly in My Plans until the user opens it.
      dayKey(new Date(p.created_at).getTime()) === todayK &&
      (Date.now() - new Date(p.created_at).getTime()) > 60 * 60 * 1000 &&
      !isSnoozedToday(p.id)
    )
  }, [plans])

  // Memoized so the context value's identity is stable — `usePlans()` is read per row
  // in My List, so a fresh literal here re-rendered every ListRow on any plan change.
  // All callbacks are useCallback-stable (isPlanned now too), so this only changes on
  // real plan-data changes.
  return useMemo(
    () => ({ plans, futurePlans, loaded, todayPending, needsCheckIn, toggle, confirm, confirmMany, skip, later, isPlanned, reload: load }),
    [plans, futurePlans, loaded, todayPending, needsCheckIn, toggle, confirm, confirmMany, skip, later, isPlanned, load]
  )
}

// ── FilmReelIcon ───────────────────────────────────────────────────────────────
// Custom SVG film reel. Spins + glows amber when active, static gray when not.
// The reel has: outer ring, hub, 3 perforations at 120°, 3 spokes, 6 sprocket holes.
export function FilmReelIcon({ active, spinning, size = 18 }) {
  const amber = 'var(--amber)'
  const gray  = 'rgba(var(--ink-rgb),0.32)'
  const c     = active ? amber : gray
  // Six sprocket hole positions around the outer ring
  const holes = [0, 60, 120, 180, 240, 300].map(deg => {
    const rad = (Math.PI * deg) / 180
    return { x: 12 + 8.5 * Math.sin(rad), y: 12 - 8.5 * Math.cos(rad), deg }
  })
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
      style={{ display: 'block', '--nxa': spinning ? 'reelSpinTM 1.5s linear infinite' : 'none' }}>
      {/* Outer ring */}
      <circle cx={12} cy={12} r={10.5} stroke={c} strokeWidth={1.5}/>
      {/* Glow ring when active */}
      {active && <circle cx={12} cy={12} r={10.5} stroke={amber} strokeWidth={4} opacity={0.12}/>}
      {/* Three spokes to perforations */}
      {[90, 210, 330].map(deg => {
        const rad = (Math.PI * deg) / 180
        const x2 = 12 + 6 * Math.cos(rad)
        const y2 = 12 + 6 * Math.sin(rad)
        return <line key={deg} x1={12} y1={12} x2={x2} y2={y2} stroke={c} strokeWidth={1.2} opacity={0.55}/>
      })}
      {/* Three large perforations at 120° */}
      {[90, 210, 330].map(deg => {
        const rad = (Math.PI * deg) / 180
        const x = 12 + 6.2 * Math.cos(rad)
        const y = 12 + 6.2 * Math.sin(rad)
        return <circle key={deg} cx={x} cy={y} r={1.7} fill={c} opacity={0.88}/>
      })}
      {/* Hub circle */}
      <circle cx={12} cy={12} r={2.8} fill={c} opacity={0.92}/>
      {/* Hub hole */}
      <circle cx={12} cy={12} r={1.2} fill="#07070f"/>
      {/* Sprocket holes around outer ring */}
      {holes.map(({ x, y, deg }) => (
        <rect key={deg}
          x={x - 1} y={y - 1.5}
          width={2} height={3} rx={0.5}
          fill={c} opacity={0.5}
          transform={`rotate(${deg} ${x} ${y})`}
        />
      ))}
    </svg>
  )
}

// ── WatchPlanButton ────────────────────────────────────────────────────────────
// Moon toggle: scales + glows amber when a plan is set, static gray when not.
// Replaces the 🌙 emoji which had no visual on/off distinction. Uses the
// canonical MoonIcon (same glyph as the bottom nav's Tonight tab) — its
// 2026-08-06 redraw dropped the crescent cutout that used to read as a
// capital "C" at this button's small size, so it's safe to use here again,
// this time with its `active` prop wired to fire the ring-bloom/star-jitter
// burst on the same tap that scales the button.
export function WatchPlanButton({ show, season, episode, episodeName, plannedDate, plans, style = {} }) {
  const ctx     = usePlans()
  const planned = ctx?.isPlanned(show?.id, season, episode) ?? false
  const isFuture = !!plannedDate && plannedDate > todayKey()
  const [anim,  setAnim]  = useState(false)
  const [toast, setToast] = useState(null)

  const handleTap = async (e) => {
    e.stopPropagation()
    if (!ctx || !show) return
    setAnim(true)
    setTimeout(() => setAnim(false), 600)
    // ctx.toggle is optimistic — `planned` flips immediately from context state.
    const added = await ctx.toggle(show, season, episode, episodeName, plannedDate)
    if (added == null) return
    setToast(added ? 'added' : 'removed')
    setTimeout(() => setToast(null), 2200)
  }

  return (
    <div style={{ position: 'relative', display: 'inline-flex', ...style }}>
      <button
        onTouchEnd={e => { e.preventDefault(); handleTap(e) }}
        onClick={handleTap}
        title={planned ? 'Remove from plans' : (isFuture ? 'Plan for its air date' : 'Add to tonight')}
        style={{
          width: 34, height: 34, borderRadius:'var(--radius-full)',
          background: planned ? 'rgba(var(--amber-rgb),0.18)' : 'var(--surface)',
          border: planned ? '1.5px solid rgba(var(--amber-rgb),0.5)' : '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', flexShrink: 0,
          transform: anim ? 'scale(1.3)' : 'scale(1)',
          transition: 'transform 0.2s cubic-bezier(0.34,1.7,0.64,1), background 0.15s, border 0.15s',
          boxShadow: planned ? '0 0 14px rgba(var(--amber-rgb),0.35)' : 'none',
        }}
      >
        <MoonIcon size={17}
          color={planned ? 'var(--amber)' : 'rgba(var(--ink-rgb),0.55)'}
          active={anim} />
      </button>
      {toast && (
        <div style={{
          position: 'absolute', bottom: 42, left: '50%', transform: 'translateX(-50%)',
          background: toast === 'added' ? 'rgba(var(--amber-rgb),0.95)' : 'rgba(60,60,80,0.95)',
          color: toast === 'added' ? 'var(--amber-ink)' : 'var(--text-strong)',
          borderRadius:'var(--radius-xl)', padding: '4px 10px',
          fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
          fontFamily: 'var(--font)', zIndex: 100,
          '--nxa': 'toastPop 0.25s cubic-bezier(0.34,1.4,0.64,1)',
          pointerEvents: 'none',
        }}>
          {toast === 'added' ? (isFuture ? 'Planned!' : 'Tonight!') : 'Removed'}
        </div>
      )}
      <style>{'@keyframes toastPop{from{opacity:0;transform:translateX(-50%) scale(0.7)}to{opacity:1;transform:translateX(-50%) scale(1)}}'}</style>
    </div>
  )
}

// ── TonightWidget ──────────────────────────────────────────────────────────────
// Glowing home screen card showing tonight's committed episodes.
// Appears above UpNext when there are pending plans.

export function TonightWidget({ plans, onTap, userId, onWatched }) {
  const navigate = useNavigate()
  const ctx = usePlans()          // for toggle() — swipe-left drops a row from tonight
  const { collapsed, toggle } = useUiPrefs(userId)
  const isCol = collapsed('home.tonightLineup')
  const [doneIds, setDoneIds] = useState([])   // optimistic fade as a row is marked watched
  if (!plans?.length) return null

  const handleWatched = (e, plan) => {
    e.stopPropagation()
    if (doneIds.includes(plan.id)) return
    setDoneIds(prev => [...prev, plan.id])
    // Hold the actual advance ~650ms so the clapboard's slam+twinkle plays before
    // the row dims/resolves (matches the Tonight hub's treatment) (Jason 2026-06-29).
    setTimeout(() => onWatched?.(plan), 650)
  }

  return (
    <div style={{
      margin: '0 0 18px',
      background: 'linear-gradient(135deg, rgba(var(--amber-rgb),0.1), rgba(var(--amber-rgb),0.04))',
      border: '1.5px solid rgba(var(--amber-rgb),0.25)',
      borderRadius: 18,
      padding: '14px 16px',
      fontFamily: 'var(--font)',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Ambient glow */}
      <div style={{
        position: 'absolute', top: -20, right: -20, width: 100, height: 100,
        background: 'radial-gradient(circle, rgba(var(--amber-rgb),0.15) 0%, transparent 70%)',
        pointerEvents: 'none',
      }}/>

      <div onClick={() => toggle('home.tonightLineup')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isCol ? 0 : 10, cursor: 'pointer', userSelect: 'none', WebkitUserSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          {/* Fixed moon, it's "Tonight's", so a time-of-day emoji (a SUN in the
              morning) contradicted the label (Jason 2026-06-29). Uses the canonical
              MoonIcon: this file's own WatchPlanButton and the bottom-nav Tonight tab
              use the same glyph, so this stays the same moon everywhere on screen
              rather than a second, mismatched one (Jason 2026-07-24, updated 2026-08-06
              when MoonIcon's crescent-cutout redraw made it safe to use here too). */}
          <MoonIcon size={15} color="var(--amber)" />
          <span style={{ color: 'var(--amber)', fontWeight: 700, fontSize: 13 }}>
            Tonight's Lineup
          </span>
          <span style={{
            background: 'rgba(var(--amber-rgb),0.15)', color: 'var(--amber)',
            borderRadius:'var(--radius-xl)', padding: '1px 7px', fontSize: 10, fontWeight: 700,
          }}>
            {plans.length}
          </span>
        </div>
        {/* "Plan more" (navigates) sat ~10px from the collapse chevron — two DIFFERENT
            actions close enough to mis-tap (Jason 2026-07-25). Give it a bordered pill
            (a clear, larger, self-contained target) and widen the gap so aiming at one
            can't accidentally hit the other / the header's own collapse toggle. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            className="ui-primary"
            onClick={(e) => { e.stopPropagation(); navigate('/tonight') }}
            style={{ background: 'rgba(var(--amber-rgb),0.12)', border: '1px solid rgba(var(--amber-rgb),0.24)',
              color: 'var(--amber)', fontWeight: 700, fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font)',
              padding: '6px 11px', borderRadius: 'var(--radius-pill)', whiteSpace: 'nowrap' }}
          >
            Plan more →
          </button>
          <span aria-hidden="true" style={{ display: 'inline-flex', padding: 4, marginRight: -4 }}>
            <ChevronDown size={16} style={{ color: 'var(--text-dim)', transition: 'transform 0.2s', transform: isCol ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
          </span>
        </div>
      </div>

      <Collapse open={!isCol}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {plans.map(plan => {
          const done = doneIds.includes(plan.id)
          return (
          // Swipe left to drop it from tonight. Changing your mind used to mean
          // navigating back to the show page and un-tapping the moon — the plan was
          // visible here but only editable somewhere else (Jason 2026-08-01). The
          // Uses toggle (delete the row), NOT skip (status='skipped'), to match the ×
          // on /tonight exactly. There's a unique index on
          // (user_id, show_id, season, episode, planned_date), so a skipped row keeps
          // occupying that slot for the rest of the day: the moon reads unplanned
          // (isPlanned requires 'pending') but re-planning the same episode then has
          // to clear the ghost first, costing a silent wasted tap. skip() belongs to
          // the evening check-in, where the record is the point and nobody's
          // re-planning. One action, one behaviour, both places.
          //
          // "Not tonight", not "Delete", and amber rather than red: this drops the
          // PLAN, the show stays on your list untouched. A trash can here would
          // scare people off the one control that makes the lineup feel safe to fill.
          <SwipeRow
            key={plan.id}
            onTap={() => navigate(`/show/${plan.show_id}`)}
            onDelete={() => ctx?.toggle?.(
              { id: plan.show_id, ...plan.show_data },
              plan.season, plan.episode, plan.episode_name
            )}
            deleteLabel="Not tonight"
            DeleteIcon={MoonStar}
            deleteTone="var(--amber)"
            deleteInk="var(--amber-ink)"   /* dark on gold; white was unreadable */
          >
          <div
            className="tap-card"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              cursor: 'pointer', padding: '6px 0', borderRadius: 'var(--radius-sm)',
              opacity: done ? 0.45 : 1, transition: 'opacity 0.3s',
              borderBottom: plans.indexOf(plan) < plans.length - 1
                ? '1px solid var(--border-soft)' : 'none',
            }}
          >
            {plan.show_data?.poster_path && (
              <img
                src={`https://image.tmdb.org/t/p/w92${plan.show_data.poster_path}`}
                alt={plan.show_data.name}
                style={{ width: 32, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'var(--text-strong)', fontWeight: 600, fontSize: 13, marginBottom: 1,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {plan.show_data?.name}
              </div>
              <div style={{ color: 'var(--text-dim)', fontSize: 11 }}>
                S{plan.season}·E{plan.episode}
                {(plan.episode_name || plan.show_data?.next_episode_name) ? `, ${plan.episode_name || plan.show_data?.next_episode_name}` : ''}
              </div>
            </div>
            {/* End point now matches intent: launch the streaming app (renders only
                when the show is on one of your services) + a ✓ to mark it watched and
                clear it from tonight. Replaces the spinning film-reel that read as a
                loading spinner and only opened the show page (Jason 2026-06-29). */}
            {/* data-noswipe: SwipeRow bails out of its gesture when the pointer starts
                inside this, so the launch + clapboard buttons stay tappable instead of
                being read as the start of a drag. */}
            <div data-noswipe style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
              <StreamingLaunchButton showId={plan.show_id} showName={plan.show_data?.name} season={plan.season} size={30} />
              {/* Animated clapboard mark-watched, the arm slams + twinkles on tap.
                  Matches the Tonight hub's control (was a plain ✓); leveling up to the
                  more on-brand, delightful one (Jason 2026-06-29). */}
              <button
                onClick={(e) => handleWatched(e, plan)}
                title={done ? 'Watched' : 'Mark watched'}
                style={{ background: 'none', border: 'none', cursor: done ? 'default' : 'pointer', padding: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                <Clapboard watched={done} size={28} accent='var(--green)' idle='rgba(var(--amber-rgb),0.85)' />
              </button>
            </div>
          </div>
          </SwipeRow>
        )})}
      </div>
      </Collapse>
    </div>
  )
}

// ── FriendPlansWidget ──────────────────────────────────────────────────────────
// "Friends watching tonight" on the People tab + Home. Shows followed friends who
// either planned an episode for tonight OR actually watched one today. The old
// version only read explicit watch_plans AND queried EVERY user (no follows
// filter), so it was simultaneously a privacy leak and near-always empty — most
// people watch without pressing the 🌙 plan button (Jason: "Melissa watches every
// night" but never showed). Now it merges plans + today's public episode activity,
// scoped to people you follow (Jason 2026-06-29).

export function FriendPlansWidget({ user, onHome = false }) {
  const [entries, setEntries] = useState([])
  const [isGlobal, setIsGlobal] = useState(false)  // showing network-wide public activity (no follows)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const today = todayKey()

  useEffect(() => {
    if (!user?.id) { setLoading(false); return }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      // Who I follow — the feature is "FRIENDS watching", so scope to them.
      const { data: fRows } = await supabase.from('follows').select('following_id').eq('follower_id', user.id)
      const ids = (fRows || []).map(r => r.following_id)
      if (!ids.length) {
        // Follow-less user: fall back to PUBLIC episode activity across NextEp so the
        // "tonight" surface isn't a dead end. Public-only rows, so no privacy leak.
        const start = new Date(); start.setHours(0, 0, 0, 0)
        const { data: acts } = await supabase.from('activities')
          .select('id, user_id, show_id, show_name, show_poster, data, created_at')
          .in('type', ['episode', 'binge'])
          .filter('data->>public', 'eq', 'true')
          .neq('user_id', user.id)
          .gte('created_at', start.toISOString())
          .order('created_at', { ascending: false }).limit(30)
        const uids = [...new Set((acts || []).map(a => a.user_id))]
        const pmap = await fetchProfiles(uids)   // shared session cache
        const seen = new Map()
        for (const a of (acts || [])) {
          const k = `${a.user_id}-${a.show_id}`
          if (!seen.has(k)) seen.set(k, {
            key: 'a' + a.id, userId: a.user_id, prof: pmap[a.user_id],
            showId: a.show_id, showName: a.show_name, poster: a.show_poster,
            season: a.data?.season, episode: a.data?.episode, kind: 'watched',
          })
        }
        if (!cancelled) { setEntries([...seen.values()].slice(0, 10)); setIsGlobal(true); setLoading(false) }
        return
      }

      // Profiles for everyone we follow — via the shared session cache.
      const profsP = fetchProfiles(ids)
      // Explicit "plan for tonight" rows.
      const plansP = supabase.from('watch_plans')
        .select('id, user_id, show_id, show_data, season, episode')
        .in('user_id', ids).eq('planned_date', today).eq('status', 'pending')
        .order('created_at', { ascending: false }).limit(20)
      // Episodes friends actually watched today (public activity only). Local-day
      // start as a UTC instant so the timestamp comparison is correct.
      const start = new Date(); start.setHours(0, 0, 0, 0)
      const actsP = supabase.from('activities')
        .select('id, user_id, show_id, show_name, show_poster, data, created_at')
        .in('user_id', ids).in('type', ['episode', 'binge'])
        .filter('data->>public', 'eq', 'true')
        .gte('created_at', start.toISOString())
        .order('created_at', { ascending: false }).limit(30)

      const [pmap, { data: plans }, { data: acts }] = await Promise.all([profsP, plansP, actsP])
      if (cancelled) return

      const watched = (acts || []).map(a => ({
        key: 'a' + a.id, userId: a.user_id, prof: pmap[a.user_id],
        showId: a.show_id, showName: a.show_name, poster: a.show_poster,
        season: a.data?.season, episode: a.data?.episode, kind: 'watched',
      }))
      const planned = (plans || []).map(p => ({
        key: 'p' + p.id, userId: p.user_id, prof: pmap[p.user_id],
        showId: p.show_id, showName: p.show_data?.name, poster: p.show_data?.poster_path,
        season: p.season, episode: p.episode, kind: 'plan',
      }))
      // Dedupe by person+show, preferring the concrete "watched" over a plan.
      const seen = new Map()
      for (const e of [...watched, ...planned]) {
        const k = `${e.userId}-${e.showId}`
        if (!seen.has(k)) seen.set(k, e)
      }
      setEntries([...seen.values()].slice(0, 10))
      setIsGlobal(false)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [user?.id, today])

  // While loading, show a shimmer instead of flashing the "no friends" message —
  // a false negative on every open was the most-felt un-premium tell (Track 3).
  if (loading) {
    if (onHome) return null
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0' }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 2px' }}>
            <div className="skel" style={{ width: 38, height: 38, borderRadius: 'var(--radius-full)', flexShrink: 0, animationDelay: `${i * 0.12}s` }} />
            <div style={{ flex: 1 }}>
              <div className="skel" style={{ width: '45%', height: 11, borderRadius: 6, marginBottom: 6, animationDelay: `${i * 0.12 + 0.06}s` }} />
              <div className="skel" style={{ width: '65%', height: 9, borderRadius: 5, animationDelay: `${i * 0.12 + 0.12}s` }} />
            </div>
          </div>
        ))}
      </div>
    )
  }
  if (!entries.length && onHome) return null
  if (!entries.length) return (
    <div style={{ textAlign: 'center', padding: '44px 28px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontFamily: 'var(--font)', '--nxa': 'pageSlideUp 0.4s var(--ease-out) backwards' }}>
      <div style={{ fontSize: 44, lineHeight: 1, marginBottom: 4, filter: 'drop-shadow(0 6px 18px rgba(var(--amber-rgb),0.28))', '--nxa': 'trophyFloat 3.4s ease-in-out infinite' }}>🌙</div>
      <div style={{ color: 'var(--text)', fontSize: 15.5, fontWeight: 600, fontFamily: 'var(--font-serif)', letterSpacing: '0.01em' }}>
        {isGlobal ? 'A quiet night on NextEp' : "Nobody's watching yet tonight"}
      </div>
      <div style={{ color: 'var(--text-dim)', fontSize: 13, lineHeight: 1.55, maxWidth: 270 }}>
        {isGlobal
          ? 'No episodes logged across NextEp yet tonight. Press play and be the first. 🍿'
          : 'Follow a few people or invite a friend, and their tonight lineup lands right here.'}
      </div>
    </div>
  )

  return (
    <div style={{ fontFamily: 'var(--font)' }}>
      {onHome && <div style={{ color:'var(--text-dim)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', margin:'4px 0 10px' }}>{isGlobal ? 'Watching on NextEp tonight' : 'Friends watching tonight'}</div>}
      {entries.map(e => {
        const prof = e.prof
        const name = prof?.display_name || (prof?.username ? `@${prof.username}` : 'Someone')
        return (
          <div key={e.key} className="tap-card" style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 0', borderBottom: '1px solid var(--border-soft)',
            cursor: 'pointer', borderRadius: 'var(--radius-sm)',
          }} {...clickable(() => e.showId && navigate(`/show/${e.showId}`))}>
            <Avatar name={name} avatarUrl={prof?.avatar_url} size={36} ring="rgba(var(--amber-rgb),0.2)" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'var(--text-strong)', fontSize: 13, fontWeight: 500, marginBottom: 2 }}>
                <span style={{ color: 'var(--amber)', fontWeight: 700 }}>{name}</span>
                {e.kind === 'watched' ? ' watched ' : ' is watching '}
                <span style={{ fontWeight: 600 }}>{e.showName || 'a show'}</span>
                {e.kind === 'watched' ? ' tonight 🍿' : ' tonight 🌙'}
              </div>
              {e.season != null && e.episode != null && (
                <div style={{ color: 'var(--text-dim)', fontSize: 11 }}>
                  S{e.season}·E{e.episode}
                </div>
              )}
            </div>
            {e.poster && (
              <img src={`https://image.tmdb.org/t/p/w92${e.poster}`} alt=""
                style={{ width: 28, height: 42, borderRadius: 5, objectFit: 'cover', flexShrink: 0, opacity: 0.8 }}/>
            )}
          </div>
        )
      })}
    </div>
  )
}


// ── CheckInController ─────────────────────────────────────────────────────────
// Global modal controller. Lives inside PlansProvider so it has context access.
// Fires PlansCheckIn when a plan has been pending for 4+ hours (episode should
// have aired by now). Works on any page — not just Home.
// Also handles calling saveProgress to actually mark the episode watched.

export function CheckInController({ user, wl }) {
  const planCtx        = usePlans()
  const [batch, setBatch] = useState(null)   // the plans being checked in (one sheet)
  const shownRef       = useRef(false)        // already surfaced this session

  // Surface ONE consolidated check-in for all of tonight's due plans, instead of
  // a separate full-screen prompt per plan (the old 7-plans → 7-popups behavior).
  useEffect(() => {
    if (batch || shownRef.current) return
    const due = planCtx?.needsCheckIn || []
    if (due.length) { shownRef.current = true; setBatch(due) }
  }, [planCtx?.needsCheckIn, batch])

  // Wait for a clear slot so the check-in doesn't stack on What's New / the update
  // prompt at load. Lower priority than those (it's a task, not an action).
  const slot = usePromptSlot({ id: 'checkin', want: !!(batch && user), priority: 50 })

  if (!batch || !user || !slot) return null

  const close = () => {
    // Snooze whatever's still pending so it won't re-nag today (it rolls forward
    // tomorrow). Confirmed ones are already settled.
    batch.forEach(p => snoozeToday(p.id))
    setBatch(null)
  }

  return (
    <PlansCheckIn
      plans={batch}
      user={user}
      wl={wl}
      planCtx={planCtx}
      onClose={close}
    />
  )
}

// ── PlansProvider ──────────────────────────────────────────────────────────────
export function PlansProvider({ user, promoteFromPlan, children }) {
  const plans = useWatchPlans(user, promoteFromPlan)
  return <PlansCtx.Provider value={plans}>{children}</PlansCtx.Provider>
}
